import { useState, useCallback, useRef } from 'react';
import { toHex } from './scannerCommands';

/**
 * Hook for Web Serial API communication with the SQ400H scanner
 * Works only in Chrome/Edge with USB CDC mode enabled on scanner
 */
export function useWebSerial() {
  const [isConnected, setIsConnected] = useState(false);
  const [isSupported] = useState(() => 'serial' in navigator);
  const [error, setError] = useState(null);
  const [log, setLog] = useState([]);
  
  const portRef = useRef(null);
  const readerRef = useRef(null);
  const readableStreamClosed = useRef(null);

  const addLog = useCallback((entry) => {
    setLog((prev) => [{ ...entry, timestamp: new Date().toISOString() }, ...prev].slice(0, 200));
  }, []);

  const connect = useCallback(async (baudRate = 9600) => {
    if (!isSupported) {
      setError('Web Serial API not supported');
      return false;
    }

    try {
      const port = await navigator.serial.requestPort({
        filters: [
          { usbVendorId: 0x1F3A },  // Allwinner / SQ400H scanner
          { usbVendorId: 0x1FC9 },  // NXP
          { usbVendorId: 0x1A86 },  // CH340 USB-Serial
        ],
      });

      await port.open({
        baudRate,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none',
      });

      portRef.current = port;
      setIsConnected(true);
      setError(null);

      addLog({
        type: 'system',
        message: `Connected at ${baudRate} baud`,
        direction: 'info',
      });

      // Start reading
      startReading(port);

      return true;
    } catch (err) {
      if (err.name !== 'NotFoundError') {
        setError(err.message);
        addLog({ type: 'error', message: err.message, direction: 'error' });
      }
      return false;
    }
  }, [isSupported, addLog]);

  const startReading = useCallback(async (port) => {
    while (port.readable) {
      const reader = port.readable.getReader();
      readerRef.current = reader;

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value) {
            addLog({
              type: 'rx',
              message: toHex(value),
              raw: Array.from(value),
              direction: 'in',
            });
          }
        }
      } catch (err) {
        if (err.name !== 'NetworkError') {
          console.error('Read error:', err);
        }
      } finally {
        reader.releaseLock();
      }
    }
  }, [addLog]);

  const disconnect = useCallback(async () => {
    try {
      if (readerRef.current) {
        await readerRef.current.cancel();
        readerRef.current = null;
      }
      if (portRef.current) {
        await portRef.current.close();
        portRef.current = null;
      }
      setIsConnected(false);
      addLog({ type: 'system', message: 'Disconnected', direction: 'info' });
    } catch (err) {
      console.error('Disconnect error:', err);
    }
  }, [addLog]);

  const sendCommand = useCallback(async (data) => {
    if (!portRef.current?.writable) {
      setError('Not connected');
      return false;
    }

    try {
      const writer = portRef.current.writable.getWriter();
      const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
      await writer.write(bytes);
      writer.releaseLock();

      addLog({
        type: 'tx',
        message: toHex(bytes),
        raw: Array.from(bytes),
        direction: 'out',
      });

      return true;
    } catch (err) {
      setError(err.message);
      addLog({ type: 'error', message: `Send failed: ${err.message}`, direction: 'error' });
      return false;
    }
  }, [addLog]);

  const clearLog = useCallback(() => setLog([]), []);

  return {
    isConnected,
    isSupported,
    error,
    log,
    connect,
    disconnect,
    sendCommand,
    clearLog,
  };
}
